import type { ReactNode } from 'react';
import { Inbox, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * What a screen shows when there is nothing on it. It is never a shrug: an empty screen either names the
 * action that would fill it, or says why the data is missing. "Nothing here" and "no data yet" are different
 * facts, and a reporting screen must never present the second as a zero.
 *
 * Ported from the HyperTek prototype's `components/states/empty-state.tsx`: an identity-tinted icon box, one
 * line in the body size, an optional action, `p-8` and a `gap-3` rhythm. `layout="box"` is the dashed-bordered
 * box that stands on a page; `layout="fill"` is the borderless centred fill for a section inside a card, where
 * the card is already the frame.
 *
 * Product deltas: the title stays an `h2`, because screens and their tests find an empty section by its heading;
 * it may carry a second sentence (`children`) saying why or what next, in the muted tone under it; and the action
 * is the screen's own control (often a link), not a callback. The default is `box`, the form every full-page
 * empty state here takes.
 */
export function EmptyState({
  title, children, action, icon: Icon = Inbox, iconBox = 'h-icon-box-violet', layout = 'box', className,
}: {
  title: string;
  children?: ReactNode;
  action?: ReactNode;
  /** The icon-box glyph, rendered `size-5` inside the tint. */
  icon?: LucideIcon;
  /** The `.h-icon-box-{colour}` utility for the identity tint. */
  iconBox?: string;
  layout?: 'box' | 'fill';
  className?: string;
}) {
  return (
    <div
      data-slot="empty-state"
      className={cn(
        'flex flex-col items-center gap-3 p-8 text-center',
        layout === 'box'
          ? 'rounded-[var(--h-radius-md)] border border-dashed border-[hsl(var(--h-border))]'
          : 'flex-1 justify-center',
        className,
      )}
    >
      <span className={iconBox} aria-hidden="true">
        <Icon className="size-5" />
      </span>
      <h2 className="text-sm text-[hsl(var(--h-card-foreground))]">{title}</h2>
      {children && <p className="max-w-[60ch] text-sm text-[hsl(var(--h-muted-foreground))]">{children}</p>}
      {action && <div className="flex flex-wrap items-center justify-center gap-3">{action}</div>}
    </div>
  );
}
